import { memo, useCallback, useMemo, useState } from "react";
import { AssignTo } from "../../../../components/AssignTo/AssignTo";
import Demo from "./Demo/Demo";
import Name from "./Name/Name";
import Highlight from "./Highlight/Highlight";
import Actions from "./Actions/Actions";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useMyTeam,
  useTeamsByStore,
} from "@coworker/app/src/core/hooks/useStoreTeams";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import {
  clean,
  makeMatcher,
} from "@coworker/components/src/helpers/string.helpers";

function AssignToDemo() {
  const [assignedTeamId, setAssignedTeamId] = useState<string | null>(null);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const storeId = useStoreId();

  const { data: teams, isLoading: isTeamsLoading } = useTeamsByStore(
    storeId || ""
  );

  const { data: activeUsers, isLoading: isUsersLoading } =
    useActiveStoreUsers();
  const myTeam = useMyTeam();

  const assignableTeams = useMemo(() => {
    return teams?.filter((team) => team.status === "ACTIVE");
  }, [teams]);

  const sortedTeams = useMemo(() => {
    return assignableTeams?.sort((teamA, teamB) =>
      teamA.name.localeCompare(teamB.name)
    );
  }, [assignableTeams]);

  const matchAll = useMemo(() => {
    return makeMatcher(searchQuery, true);
  }, [searchQuery]);

  const matcher = useCallback(
    (query: string) => {
      return !matchAll || matchAll.test(clean(query));
    },
    [matchAll]
  );

  const matchingTeams = useMemo(() => {
    return sortedTeams?.filter((team) => matcher(team.name));
  }, [matcher, sortedTeams]);

  const matchingUsers = useMemo(() => {
    return (activeUsers || []).filter((user) => matcher(user.fullName));
  }, [activeUsers, matcher]);

  const description = useMemo(() => {
    return assignedUserId
      ? (activeUsers || []).find((user) => user.uid === assignedUserId)
          ?.fullName || ""
      : assignedTeamId
        ? teams?.find((team) => team.id === assignedTeamId)?.name || ""
        : "";
  }, [activeUsers, assignedTeamId, assignedUserId, teams]);

  const handleConfirmClick = useCallback(
    ({
      assignedTeamId,
      assignedUserId,
    }: {
      assignedTeamId: string;
      assignedUserId: string | null;
    }) => {
      setAssignedTeamId(assignedTeamId);
      setAssignedUserId(assignedUserId);
    },
    []
  );

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const isError =
    showValidation && assignedTeamId === null && assignedUserId === null;

  return (
    <Demo>
      <Name>AssignTo</Name>
      <Highlight>
        <FixaListView id="assigntodemo" size="small">
          <AssignTo
            assignedTeamId={assignedTeamId}
            assignedUserId={assignedUserId}
            disabled={disabled}
            description={description}
            searchQuery={searchQuery}
            matchingTeams={matchingTeams || []}
            isTeamsLoading={isTeamsLoading}
            myTeamId={myTeam?.id || ""}
            matchingUsers={matchingUsers || []}
            isUsersLoading={isUsersLoading}
            teams={assignableTeams || []}
            isError={isError}
            onConfirmClick={handleConfirmClick}
            onSearchQueryChange={handleSearchQueryChange}
          />
        </FixaListView>
      </Highlight>
      <Actions>
        <FixaCheckbox
          label="disabled"
          value="assigntodemo-disabled"
          id="assigntodemo-disabled"
          checked={disabled}
          onChange={() => setDisabled((prev) => !prev)}
        />
        <FixaCheckbox
          label="showValidation"
          value="assigntodemo-showValidation"
          id="assigntodemo-showValidation"
          checked={showValidation}
          onChange={() => setShowValidation((prev) => !prev)}
        />
      </Actions>
    </Demo>
  );
}

export default memo(AssignToDemo);
